import { createContext, useContext, useEffect, useState } from 'react'
import { useAppDispatch } from '../storeHooks/storehooks'
import { setValue } from '@/redux-store/slices/menuItemSlice/menuItemSlice'

type Theme = 'dark' | 'light' | 'high-contrast-light' | 'high-contrast-dark'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme?: (theme: Theme) => void
}

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(
  undefined,
)

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
  )

  const dispatch = useAppDispatch()

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove(
      'light',
      'dark',
      'high-contrast-light',
      'high-contrast-dark',
    )

    root.classList.add(theme)
    dispatch(setValue(theme))
  }, [theme])

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme)
      setTheme(newTheme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
